import Colors from '@omni/kit/theming/Colors';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { SizeClassV2 } from '../../../contexts/SizeClassContext';
import BorderRadius from '../../../theming/BorderRadius';
import Spacing from '../../../theming/Spacing';
import KitFadeView from '../../KitFadeView/KitFadeView';
import KitImageContainer from '../../KitImageContainer/KitImageContainer';
import KitProgress from '../../KitProgress';
import { KitPlayerProps } from '../KitPlayer.types';
import BasicInfo from './Controls/components/BasicInfo';
import LiveTag from './Controls/components/LiveTag';
import { PrePlayPosterData } from './PrePlayPoster.types';

const GRADIENT_ADDITIONAL_HEIGHT = Spacing.xxxl;

interface PrePlayPosterProps extends PrePlayPosterData {
  branding?: KitPlayerProps['branding'];
  show: boolean;
  title?: string;
  subtitle?: string;
  containerSize: SizeClassV2;
  isLive?: boolean;
}

export default function PrePlayPoster({
  branding,
  color,
  url,
  progressMs,
  durationMs,
  show = true,
  title,
  subtitle,
  containerSize,
  isLive = false,
}: PrePlayPosterProps): ReactElement | null {
  const { silhouetteLogo = '', iconBackgroundColor = '' } = branding || {};

  const [gradientHeight, setGradientHeight] = useState<number | undefined>(0);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { height, y } = event.nativeEvent.layout;

    setGradientHeight(height + y + GRADIENT_ADDITIONAL_HEIGHT);
  }, []);

  const gradientStyle = useMemo(() => {
    if (!gradientHeight) return {};

    return { height: gradientHeight };
  }, [gradientHeight]);

  const showGradient = useMemo(() => {
    return title || subtitle || isLive;
  }, [title, subtitle, isLive]);

  return (
    <KitFadeView show={show} style={[StyleSheet.absoluteFill]}>
      <KitImageContainer
        backgroundColor={color}
        imageUrl={url}
        logoBackgroundColor={iconBackgroundColor}
        logoUrl={silhouetteLogo}
        borderRadius={BorderRadius.none}
      />

      {showGradient ? (
        <View style={[StyleSheet.absoluteFill, gradientStyle]}>
          <LinearGradient
            colors={['#00000080', '#00000000']}
            end={{ x: 0, y: 1 }}
            start={{ x: 0, y: 0.01 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </View>
      ) : null}

      <View
        style={[
          StyleSheet.absoluteFill,
          {
            padding: containerSize <= SizeClassV2.XS ? Spacing.l : Spacing.xl,
          },
        ]}
      >
        <View style={styles.infoContents} onLayout={onLayout}>
          <BasicInfo isVisible={true} title={title} subtitle={subtitle} />
          <LiveTag isVisible={isLive} isClickable={false} />
        </View>
      </View>
      <Progress progressMs={progressMs} durationMs={durationMs} />
    </KitFadeView>
  );
}

const Progress = ({ progressMs, durationMs }: PrePlayPosterData) => {
  const mediaProgressPercentage = useMemo(() => {
    if (!progressMs || !durationMs || durationMs === 0) {
      return 0;
    }

    return Math.round((progressMs / durationMs) * 100);
  }, [progressMs, durationMs]);

  if (!durationMs) return null;

  return (
    <LinearGradient
      colors={['#00000000', '#00000026']}
      end={{ x: 0, y: 1 }}
      start={{ x: 0, y: 0.01 }}
      style={styles.progressWrapper}
    >
      <KitProgress
        percentage={mediaProgressPercentage}
        percentageStyle={styles.percentage}
        progressContainerStyle={styles.progressContainer}
      />
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  progressWrapper: {
    borderBottomEndRadius: BorderRadius.m,
    borderBottomStartRadius: BorderRadius.m,
    bottom: 0,
    display: 'flex',
    height: 44,
    justifyContent: 'flex-end',
    left: 0,
    position: 'absolute',
    right: 0,
  },
  progressContainer: {
    backgroundColor: '#ffffff66',
    borderBottomEndRadius: BorderRadius.m,
    borderBottomStartRadius: BorderRadius.m,
    height: 3,
  },
  percentage: {
    backgroundColor: Colors.N0,
    borderBottomStartRadius: BorderRadius.m,
    height: 3,
  },
  infoContents: {
    display: 'flex',
    flexDirection: 'row',
    gap: Spacing.s,
    justifyContent: 'space-between',
    marginBottom: 'auto',
    width: '100%',
  },
});
